import { AlertCircle, Eye, Target, User, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import './RejectionModal.scss';
import { getTaxonomyLevels, colorMap } from '../taxonomyConstants';

const RejectionModal = ({
  isOpen,
  onClose,
  assessment,
  employeeName,
  onConfirm,
  isLoading = false,
  investment
}) => {
  const [reason, setReason] = useState('');
  const [requestedScore, setRequestedScore] = useState(assessment?.expectedScore || 5);

  useEffect(() => {
    if (assessment) {
      setRequestedScore(assessment.expectedScore || 5);
    }
  }, [assessment]);

  if (!isOpen || !assessment) return null;

  const handleConfirm = () => {
    if (reason.trim() && !isLoading) {
      onConfirm(reason.trim(), requestedScore);
      setReason('');
      setRequestedScore(assessment?.expectedScore || 5);
    }
  };

  const selfScore = assessment.selfScore;
  const managerScore = requestedScore;
  const gap = selfScore - managerScore;
  const taxonomyLevels = getTaxonomyLevels(investment);

  return (
    <div className="rejection-modal-overlay">
      <div className="rejection-modal-backdrop" onClick={onClose} />
      <div
        className="rejection-modal rejection-modal--centered rejection-modal--wide custom-scrollbar-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rejection-modal-header">
          <div>
            <div className="rejection-modal-title">
              <Eye className="icon-xs icon-orange" />
              <span>Submeter Revisão</span>
            </div>
            <div className="rejection-modal-subtitle">Solicite uma revisão da autoavaliação</div>
          </div>
          <button onClick={onClose} disabled={isLoading} className="rejection-modal-close">
            <X className="icon-xs" />
          </button>
        </div>

        <div className="rejection-modal-summary">
          <div className="rejection-modal-row">
            <User className="icon-xs icon-blue" />
            <span className="label">Colaborador:</span>
            <span className="value">{employeeName}</span>
          </div>

          <div className="rejection-modal-row">
            <Target className="icon-xs icon-green" />
            <span className="label">Competência:</span>
            <span className="value">{assessment.competencyName}</span>
          </div>

          <div className="rejection-modal-row">
            <span className="label">Autoavaliação:</span>
            <span className="score">{assessment.selfScore}<span className="score-suffix">/10</span></span>
            <span className="label space-left">Média da Empresa:</span>
            <span className="score">{assessment.companyAverage || 0}<span className="score-suffix">/10</span></span>
          </div>
        </div>

        <div className="rejection-modal-feedback">
          <label>
            <AlertCircle className="icon-xs icon-orange" />
            Justificativa (obrigatória)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            placeholder="Explique brevemente por que você não concorda com a nota e o que o colaborador deve considerar..."
            maxLength={500}
            required
          />
          {reason.trim().length === 0 && (
            <p className="rejection-modal-error">Por favor, forneça uma justificativa para a revisão.</p>
          )}
        </div>

        <div className="competency-detail-content section-margin-top">
          <div className="competency-detail-bg competency-detail-rounded competency-detail-block-margin">
            <h4 className="competency-detail-title-margin competency-detail-title-color competency-detail-title-size competency-detail-title-weight">
              Nível de Competência – Análise de Gap
            </h4>
            <div className="competency-detail-relative">
              <div className="competency-detail-flex competency-detail-justify-between competency-detail-labels-margin">
                {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10].map(
                  (value, index) => (
                    <div
                      key={index}
                      className="competency-detail-flex competency-detail-flex-col competency-detail-items-center competency-detail-label-size"
                    >
                      <span className="competency-detail-label-color">{value}</span>
                      <div
                        className={`competency-detail-label-tick ${value % 1 !== 0 ? 'competency-detail-label-dot' : ''}`}
                      ></div>
                    </div>
                  ),
                )}
              </div>

              <div
                className={`competency-detail-relative competency-detail-bar-height competency-detail-bar-bg competency-detail-bar-rounded competency-detail-bar-margin ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={(e) => {
                  if (isLoading) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const pct = x / rect.width;
                  const val = Math.round(pct * 20) / 2;
                  setRequestedScore(Math.max(0, Math.min(10, val)));
                }}
              >
                <div className="competency-detail-bar-absolute competency-detail-bar-inset competency-detail-bar-flex competency-detail-bar-rounded competency-detail-bar-overflow">
                  <div className="competency-detail-bar-col competency-detail-bar-red"></div>
                  <div className="competency-detail-bar-col competency-detail-bar-orange"></div>
                  <div className="competency-detail-bar-col competency-detail-bar-yellow"></div>
                  <div className="competency-detail-bar-col competency-detail-bar-purple"></div>
                  <div className="competency-detail-bar-col competency-detail-bar-blue"></div>
                  <div className="competency-detail-bar-col competency-detail-bar-green"></div>
                </div>

                <div
                  className={`competency-detail-marker competency-detail-marker-self score-${Math.floor(selfScore)}`}
                  style={{ left: `${(selfScore / 10) * 100}%` }}
                >
                  <div
                    className={`competency-detail-marker-label competency-detail-marker-label-self score-${Math.floor(selfScore)}`}
                  >
                    Autoavaliação: {selfScore}
                  </div>
                </div>

                <div
                  className="competency-detail-marker competency-detail-marker-expected-fixed"
                  style={{ left: `${((assessment.expectedScore || 0) / 10) * 100}%` }}
                >
                  <div className="competency-detail-marker-label competency-detail-marker-label-expected-fixed">
                    Nota esperada: {assessment.expectedScore || 0}
                  </div>
                </div>

                <div
                  className="competency-detail-marker competency-detail-marker-expected"
                  style={{ left: `${(managerScore / 10) * 100}%` }}
                >
                  <div className="competency-detail-marker-label competency-detail-marker-label-expected">
                    Nota gestor: {managerScore}
                  </div>
                </div>

                <input
                  type="range"
                  value={requestedScore}
                  onChange={(e) => setRequestedScore(parseFloat(e.target.value))}
                  onClick={(e) => e.stopPropagation()}
                  max={10}
                  min={0}
                  step={0.5}
                  disabled={isLoading}
                  className="competency-detail-range-input"
                />
              </div>

              <div className="competency-detail-gap-block gap-block-margin-top">
                <div className="competency-detail-flex competency-detail-items-center competency-detail-justify-between">
                  <span className="competency-detail-gap-label">Análise do Gap:</span>
                  <div className="competency-detail-flex competency-detail-gap-space">
                    {gap >= 0 ? (
                      <span className="competency-detail-gap-above">
                        +{gap.toFixed(1)} pontos acima da nota do gestor
                      </span>
                    ) : (
                      <span className="competency-detail-gap-below">
                        -{Math.abs(gap).toFixed(1)} pontos abaixo da nota do gestor
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="competency-detail-legend-grid legend-grid-margin-top">
                {taxonomyLevels.map((level) => {
                  const barClass = colorMap[level.name]?.barClass || '';
                  return (
                    <div key={level.name} className="competency-detail-legend-item">
                      <div className={`competency-detail-legend-bar ${barClass}`}></div>
                      <span className="competency-detail-legend-title">{level.name}</span>
                      <div className="competency-detail-legend-range">{level.range}</div>
                      <p className="competency-detail-legend-desc">{level.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="rejection-modal-actions actions-margin-top">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="rejection-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!reason.trim() || isLoading}
            className="rejection-confirm-btn"
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                <span>Solicitando Revisão...</span>
              </>
            ) : (
              <>
                <Eye className="icon-xs icon-orange" />
                <span>Submeter Revisão</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;
